/**
 * Kollektivvertrag für Arbeiter:innen im eisen- und metallverarbeitenden Gewerbe.
 *
 * Fassung 1.1.2026 in der von der WKO veröffentlichten Form. Die Werte ab 1.1.2027 stehen
 * im selben Vertrag und sind als eigener Wertsatz vorgemerkt — sie gelten von selbst, wo
 * ein Betrieb keinen eigenen Wert führt.
 *
 * **Seit K2 trägt die Vorlage den Wortlaut des Vertrags**, auch wo der Rechenkern bis dahin
 * anders gerechnet hat (Abgleich vom 24.9.2026). Ein Betrieb, der schon vor Teil 6 gerechnet
 * hat, führt für diese Stellen eigene Sätze mit den Bestandswerten (`bestand.ts`).
 *
 * Der erste Wertsatz gilt „von Anfang an": Für Monate vor 2026 kennt die Vorlage keine
 * älteren Beträge. Wer solche Monate nachrechnet, trägt die damaligen Sätze als eigene
 * Werte mit ihrem Stichtag ein.
 */
import { BETRIEBSZEITEN_DEFAULT } from '../konfig'
import type { KvVorlage, ZulageDef } from './typen'

/**
 * Abschnitt XIV (Zulagen und Zuschläge) und VIII/8–9 (Kilometergeld, Nächtigungsgeld). Die
 * Sätze sind Mindestsätze des Vertrags; ein Betrieb, der mehr zahlt, trägt seinen eigenen ein.
 */
const ZULAGEN: readonly ZulageDef[] = [
  {
    schluessel: 'schmutz',
    name: 'Schmutzzulage',
    kurz: 'Schm.',
    fundstelle: 'XIV/1',
    bemessung: 'erfasste_stunden',
    einheit: 'h',
    hinweis: 'Für Arbeiten mit außergewöhnlicher Verschmutzung — nur für die Stunden, in denen sie anfallen.',
  },
  {
    schluessel: 'erschwernis',
    name: 'Erschwerniszulage',
    kurz: 'Erschw.',
    fundstelle: 'XIV/2',
    bemessung: 'erfasste_stunden',
    einheit: 'h',
    hinweis: 'Für Arbeiten unter außergewöhnlicher Erschwernis — nur für die Stunden, in denen sie anfallen.',
  },
  {
    schluessel: 'gefahr',
    name: 'Gefahrenzulage',
    kurz: 'Gef.',
    fundstelle: 'XIV/3',
    bemessung: 'erfasste_stunden',
    einheit: 'h',
    hinweis: 'Für Arbeiten mit Gefährdung von Leben, Gesundheit oder körperlicher Sicherheit — nur für die Stunden, in denen sie anfallen.',
  },
  {
    schluessel: 'nachtarbeit',
    name: 'Nachtarbeitszulage',
    kurz: 'Nacht',
    fundstelle: 'XIV/5',
    bemessung: 'fensterstunden_ohne_ueberstunden',
    einheit: 'h',
    hinweis: 'Für jede Arbeitsstunde im Nachtfenster, die keine Überstunde ist. Wird aus Kommt und Geht gerechnet.',
  },
  {
    schluessel: 'schicht2',
    name: 'Schichtzulage 2. Schicht',
    kurz: 'Sch. 2',
    fundstelle: 'XIV/6',
    bemessung: 'erfasste_stunden',
    einheit: 'h',
    hinweis: 'Bei Wechselschicht für die Stunden der zweiten Schicht.',
  },
  {
    schluessel: 'schicht3',
    name: 'Schichtzulage 3. Schicht',
    kurz: 'Sch. 3',
    fundstelle: 'XIV/6',
    bemessung: 'erfasste_stunden',
    einheit: 'h',
    entfaelltBei: 'nachtarbeit',
    hinweis: 'Bei Wechselschicht für die Stunden der dritten Schicht — entfällt, soweit die Nachtarbeitszulage gebührt.',
  },
  {
    schluessel: 'vorarbeiter',
    name: 'Vorarbeiterzuschlag',
    kurz: 'Vorarb.',
    fundstelle: 'XIV/7',
    bemessung: 'markierte_tage_naz',
    einheit: 'h',
    prozent: true,
    hinweis:
      'Solange mindestens vier (Montage) bzw. sechs (Fertigung) Arbeitnehmer unterstellt sind. Ausgewiesen werden die Stunden der angehakten Tage — den Betrag rechnet die Lohnverrechnung aus dem Lohn.',
  },
  {
    schluessel: 'naechtigungsgeld',
    name: 'Nächtigungsgeld',
    kurz: 'Näch.-G.',
    fundstelle: 'VIII/9',
    bemessung: 'naechtigung',
    einheit: 'Nacht',
    hinweis: 'Je Nächtigung außer Haus — nur, wenn der Betrieb keine angemessene Unterkunft stellt.',
  },
  {
    schluessel: 'kilometergeld',
    name: 'Kilometergeld',
    kurz: 'km-Geld',
    fundstelle: 'VIII/8',
    bemessung: 'kilometer',
    einheit: 'km',
    hinweis: 'Für die erfassten Kilometer — nur bei genehmigter Fahrt mit dem eigenen Pkw.',
  },
]

export const AT_METALLGEWERBE_ARBEITER: KvVorlage = {
  schluessel: 'AT_METALLGEWERBE_ARBEITER',
  name: 'Eisen- und metallverarbeitendes Gewerbe — Arbeiter:innen',
  kurz: 'KV Metallgewerbe',
  fassung: '1.1.2026',
  quelle: 'wko.at/kollektivvertrag/kollektivvertrag-eisen-metallverarbeitenden-gewerbe-2026',
  wertsaetze: [
    {
      gueltigAb: '1900-01-01',
      werte: {
        pause_aktiv: '1',
        pause_dauer: '30',
        pause_schwelle: '6',

        // VII/1: Überstunde ist, was über die vereinbarte **tägliche** Arbeitszeit hinausgeht.
        ueberstunden_zeitraum: 'taeglich',
        ueberstunden_zeitraum_monate: '3',
        mehrarbeit_schwelle: '38.5',
        ueberstunden_schwelle: '40',
        mehrzeit_zuschlag_vollzeit: '50',
        mehrzeit_zuschlag_teilzeit: '25',
        umrechnung_zaehler: '167',
        umrechnung_nenner: '143',

        zuschlag_ueberstunde: '50',
        // XIV/8.2: ab der dritten Überstunde des Tages 100 % · XIV/8.7: Überstunden zwischen 20 und 6 Uhr 100 %.
        zuschlag_regeln: JSON.stringify([
          { art: 'ueberstunde_ab', abStunde: 3, prozent: 100 },
          { art: 'zeit', von: '20:00', bis: '06:00', prozent: 100, nur: 'ueberstunden' },
        ]),
        sonntag_prozent: '100',
        feiertag_prozent: '100',
        zuschlag_ausserhalb_betriebszeit: '50',
        betriebszeiten: JSON.stringify(BETRIEBSZEITEN_DEFAULT),
        mehrarbeit_ueber_50_ist_ueberstunde: '1',
        ueberstunde_ab_zaehlt_mehrarbeit: '1',
        ueberstunde_100_arbeitsfrei_ab_stunde: '11',
        ueberstunde_100_ab_wochenstunde: '51',
        rueckruf_prozent: '100',
        halbtag_aktiv: '1',
        halbtag_ende: '12:00',
        halbtag_prozent: '100',

        montagezulage_satz: '1.155',
        entfernungszulage_klein: '11.94',
        entfernungszulage_mittel: '30',
        entfernungszulage_gross: '62.04',
        entfernungszulage_schwelle_klein: '6',
        entfernungszulage_schwelle_mittel: '11',
        entfernungszulage_anreise_grenze: '12:00',
        entfernungszulage_rueckreise_grenze: '17:00',
        entfernungszulage_klein_mit_pause: '1',
        entfernungszulage_verpflegung_prozent: '40',

        rundung_aktiv: '0',
        rundung_schritt: '15',

        // XIV/1–3: je 0,746 € je Stunde
        zulage_schmutz_aktiv: '1',
        zulage_schmutz_satz: '0.746',
        zulage_erschwernis_aktiv: '1',
        zulage_erschwernis_satz: '0.746',
        zulage_gefahr_aktiv: '1',
        zulage_gefahr_satz: '0.746',
        // XIV/5: 22 bis 6 Uhr, soweit keine Überstunde
        zulage_nachtarbeit_aktiv: '1',
        zulage_nachtarbeit_satz: '3.348',
        zulage_nachtarbeit_von: '22:00',
        zulage_nachtarbeit_bis: '06:00',
        // XIV/6
        zulage_schicht2_aktiv: '1',
        zulage_schicht2_satz: '1.065',
        zulage_schicht3_aktiv: '1',
        zulage_schicht3_satz: '3.348',
        // XIV/7: 10 % des Lohns
        zulage_vorarbeiter_aktiv: '1',
        zulage_vorarbeiter_satz: '10',
        // VIII/9 und VIII/8 hängen an einer Bedingung, die der Betrieb kennt — in der Vorlage aus.
        zulage_naechtigungsgeld_aktiv: '0',
        zulage_naechtigungsgeld_satz: '22.05',
        zulage_kilometergeld_aktiv: '0',
        zulage_kilometergeld_satz: '0.5',
      },
    },
    {
      gueltigAb: '2027-01-01',
      werte: {
        montagezulage_satz: '1.178',
        entfernungszulage_klein: '12.18',
        entfernungszulage_gross: '63.28',
        zulage_schmutz_satz: '0.761',
        zulage_erschwernis_satz: '0.761',
        zulage_gefahr_satz: '0.761',
        zulage_nachtarbeit_satz: '3.566',
        zulage_schicht2_satz: '1.086',
        zulage_schicht3_satz: '3.566',
        zulage_naechtigungsgeld_satz: '22.49',
      },
    },
    {
      gueltigAb: '2028-01-01',
      werte: { zulage_nachtarbeit_satz: '3.783', zulage_schicht3_satz: '3.783' },
    },
    {
      gueltigAb: '2029-01-01',
      werte: { zulage_nachtarbeit_satz: '4', zulage_schicht3_satz: '4' },
    },
  ],
  zulagen: ZULAGEN,
  fundstellen: {
    pause_aktiv: '§ 11 AZG',
    pause_dauer: '§ 11 AZG',
    pause_schwelle: '§ 11 AZG',
    ueberstunden_zeitraum: 'VII/1',
    mehrarbeit_schwelle: 'VI/1',
    ueberstunden_schwelle: 'VIa',
    mehrzeit_zuschlag_vollzeit: 'VIa',
    mehrzeit_zuschlag_teilzeit: '§ 19d AZG',
    umrechnung_zaehler: 'XV/5.4',
    umrechnung_nenner: 'XIV/12',
    zuschlag_ueberstunde: 'XIV/8.1',
    zuschlag_regeln: 'XIV/8.2, 8.7',
    mehrarbeit_ueber_50_ist_ueberstunde: 'VIa',
    ueberstunde_ab_zaehlt_mehrarbeit: 'XIV/8.6',
    ueberstunde_100_arbeitsfrei_ab_stunde: 'XIV/8.3',
    ueberstunde_100_ab_wochenstunde: 'XIV/8.5',
    rueckruf_prozent: 'XIV/8.11',
    halbtag_aktiv: 'VI/23',
    halbtag_ende: 'VI/23',
    halbtag_prozent: 'VI/23, XIV/8.9',
    entfernungszulage_klein_mit_pause: 'VIII/2',
    entfernungszulage_verpflegung_prozent: 'VIII/4',
    sonntag_prozent: 'XIV/9',
    feiertag_prozent: 'XIV/8.10',
    montagezulage_satz: 'VIII/5',
    entfernungszulage_klein: 'VIII/2',
    entfernungszulage_mittel: 'VIII/3',
    entfernungszulage_gross: 'VIII/4',
    entfernungszulage_schwelle_klein: 'VIII/2',
    entfernungszulage_schwelle_mittel: 'VIII/3',
    entfernungszulage_anreise_grenze: 'VIII/4',
    entfernungszulage_rueckreise_grenze: 'VIII/4',
  },
}
